// hooks
import { useEffect, useRef } from 'react';

/** how long a field waits after the last keystroke before it commits */
export const COMMIT_DELAY_MS = 300;

/**
 * Buffers what a field wants to report, so typing stays in the field and its
 * surroundings only follow once the user pauses.
 *
 * Committing every keystroke straight up re-renders whatever reacts to the
 * value — in the register-absence dialog a single letter moved 14 nodes and
 * restarted the rollout calculation. That is invisible on an idle machine and
 * lags visibly with the developer tools open.
 *
 * The hook owns the buffer and the timer; the field keeps its own text state,
 * because what counts as "the same value" differs per field ("16," and "16"
 * are one number, two strings).
 *
 * `flush` runs on unmount as well, so closing a dialog never swallows a value
 * that was still waiting.
 */
export const useBufferedCommit = <T>(
  onCommit: (value: T) => void,
  delayMs = COMMIT_DELAY_MS
) => {
  // wrapped in an object so a legitimate null or empty string still counts as
  // something waiting to be sent
  const pendingRef = useRef<{ value: T } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // the flush must call the latest handler, not the one it was scheduled with
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  const flush = () => {
    clearTimeout(timerRef.current);
    const pending = pendingRef.current;
    if (pending) {
      pendingRef.current = null;
      onCommitRef.current(pending.value);
    }
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;

  useEffect(() => () => flushRef.current(), []);

  return {
    /** report this value once the typing pauses */
    schedule: (value: T) => {
      pendingRef.current = { value };
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => flushRef.current(), delayMs);
    },
    /** report it at once — for a change the surroundings may not lag behind on */
    commitNow: (value: T) => {
      pendingRef.current = { value };
      flushRef.current();
    },
    /** typed back to the committed value: there is nothing left to send */
    cancel: () => {
      pendingRef.current = null;
      clearTimeout(timerRef.current);
    },
    /** send what is waiting, if anything — on blur */
    flush: () => flushRef.current(),
    /** is something waiting? an external value must not overwrite it */
    hasPending: () => pendingRef.current !== null
  };
};
